import React, { useState } from 'react';
import axios from 'axios';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { API_URL } from '../config';
import AdSense from './AdSense';
import { sanitizeErrorMessage, validateInputSize } from '../utils/security';

// Set to true when you integrate Google AdSense
const SHOW_ADS = true;

const JsonViewer = () => {
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [isValid, setIsValid] = useState(null);
  const [activeTool, setActiveTool] = useState('validate');
  const [additionalInfo, setAdditionalInfo] = useState(null);
  const [loading, setLoading] = useState(false);

  const tools = [
    { id: 'validate', label: 'Validate & Format', icon: '✓' },
    { id: 'serialize', label: 'Serialize', icon: '→' },
    { id: 'deserialize', label: 'Deserialize', icon: '←' },
    { id: 'minify', label: 'Minify', icon: '⬇' },
    { id: 'escape', label: 'Escape', icon: '🔒' },
    { id: 'unescape', label: 'Unescape', icon: '🔓' },
    { id: 'to-xml', label: 'JSON to XML', icon: '🔄' },
    { id: 'soap-to-json', label: 'SOAP to JSON', icon: '🧼' },
  ];

  const handleValidate = async () => {
    // SECURITY: Client-side input validation
    if (!input || !input.trim()) {
      setError('Please enter JSON data to validate');
      return;
    }

    // SECURITY: Validate input size
    const sizeCheck = validateInputSize(input, 10);
    if (!sizeCheck.valid) {
      setError(sizeCheck.error);
      return;
    }

    setLoading(true);
    setError('');
    setOutput('');
    setAdditionalInfo(null);
    
    try {
      const response = await axios.post(`${API_URL}/validate-json/`, {
        data: input
      });
      
      if (response.data.valid) {
        setOutput(response.data.formatted);
        setError('');
        setIsValid(true);
      } else {
        // SECURITY: Sanitize error message
        setError(sanitizeErrorMessage(response.data.error));
        setIsValid(false);
        setOutput('');
      }
    } catch (err) {
      // SECURITY: Sanitize error message
      setError(sanitizeErrorMessage(err.response?.data?.error || 'An error occurred'));
      setIsValid(false);
      setOutput('');
    } finally {
      setLoading(false);
    }
  };

  const handleSerialize = async () => {
    setLoading(true);
    setError('');
    setOutput('');
    setAdditionalInfo(null);
    
    try {
      const response = await axios.post(`${API_URL}/json/serialize/`, {
        data: input
      });
      
      if (response.data.success) {
        setOutput(response.data.formatted);
        setAdditionalInfo({
          minified: response.data.minified,
          type: 'Serialized JSON'
        });
        setError('');
        setIsValid(true);
      } else {
        setError(sanitizeErrorMessage(response.data.error));
        setIsValid(false);
      }
    } catch (err) {
      setError(sanitizeErrorMessage(err.response?.data?.error || 'An error occurred'));
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeserialize = async () => {
    setLoading(true);
    setError('');
    setOutput('');
    setAdditionalInfo(null);
    
    try {
      const response = await axios.post(`${API_URL}/json/deserialize/`, {
        data: input
      });
      
      if (response.data.success) {
        setOutput(response.data.formatted);
        setAdditionalInfo({
          type: response.data.type,
          keys: response.data.keys,
          length: response.data.length
        });
        setError('');
        setIsValid(true);
      } else {
        setError(sanitizeErrorMessage(response.data.error));
        setIsValid(false);
      }
    } catch (err) {
      setError(sanitizeErrorMessage(err.response?.data?.error || 'An error occurred'));
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleMinify = async () => {
    setLoading(true);
    setError('');
    setOutput('');
    setAdditionalInfo(null);
    
    try {
      const response = await axios.post(`${API_URL}/json/minify/`, {
        data: input
      });
      
      if (response.data.success) {
        setOutput(response.data.minified);
        setAdditionalInfo({
          originalSize: response.data.original_size,
          minifiedSize: response.data.minified_size,
          savings: response.data.savings
        });
        setError('');
        setIsValid(true);
      } else {
        setError(sanitizeErrorMessage(response.data.error));
        setIsValid(false);
      }
    } catch (err) {
      setError(sanitizeErrorMessage(err.response?.data?.error || 'An error occurred'));
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleEscape = async () => {
    setLoading(true);
    setError('');
    setOutput('');
    setAdditionalInfo(null);
    
    try {
      const response = await axios.post(`${API_URL}/json/escape/`, {
        data: input
      });
      
      if (response.data.success) {
        setOutput(response.data.escaped);
        setError('');
        setIsValid(true);
      } else {
        setError(sanitizeErrorMessage(response.data.error));
        setIsValid(false);
      }
    } catch (err) {
      setError(sanitizeErrorMessage(err.response?.data?.error || 'An error occurred'));
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleUnescape = async () => {
    setLoading(true);
    setError('');
    setOutput('');
    setAdditionalInfo(null);
    
    try {
      const response = await axios.post(`${API_URL}/json/unescape/`, {
        data: input
      });
      
      if (response.data.success) {
        setOutput(response.data.unescaped);
        setError('');
        setIsValid(true);
      } else {
        setError(sanitizeErrorMessage(response.data.error));
        setIsValid(false);
      }
    } catch (err) {
      setError(sanitizeErrorMessage(err.response?.data?.error || 'An error occurred'));
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleJsonToXml = async () => {
    setLoading(true);
    setError('');
    setOutput('');
    setAdditionalInfo(null);

    try {
      const response = await axios.post(`${API_URL}/json/to-xml/`, {
        data: input
      });

      if (response.data.success) {
        setOutput(response.data.xml);
        setError('');
        setIsValid(true);
      } else {
        setError(sanitizeErrorMessage(response.data.error));
        setIsValid(false);
      }
    } catch (err) {
      setError(sanitizeErrorMessage(err.response?.data?.error || 'An error occurred'));
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleSoapToJson = async () => {
    setLoading(true);
    setError('');
    setOutput('');
    setAdditionalInfo(null);

    try {
      const response = await axios.post(`${API_URL}/xml/soap-to-json/`, {
        data: input
      });

      if (response.data.success) {
        setOutput(response.data.json);
        setAdditionalInfo({
          type: 'SOAP to JSON conversion'
        });
        setError('');
        setIsValid(true);
      } else {
        setError(sanitizeErrorMessage(response.data.error));
        setIsValid(false);
      }
    } catch (err) {
      setError(sanitizeErrorMessage(err.response?.data?.error || 'An error occurred'));
      setIsValid(false);
    } finally {
      setLoading(false);
    }
  };

  const handleProcess = () => {
    switch (activeTool) {
      case 'validate':
        handleValidate();
        break;
      case 'serialize':
        handleSerialize();
        break;
      case 'deserialize':
        handleDeserialize();
        break;
      case 'minify':
        handleMinify();
        break;
      case 'escape':
        handleEscape();
        break;
      case 'unescape':
        handleUnescape();
        break;
      case 'to-xml':
        handleJsonToXml();
        break;
      case 'soap-to-json':
        handleSoapToJson();
        break;
      default:
        handleValidate();
    }
  };

  const getPlaceholder = () => {
    switch (activeTool) {
      case 'serialize':
        return '{"name": "John", "age": 30} or Python dict/list';
      case 'deserialize':
        return '{"name": "John", "age": 30}';
      case 'minify':
        return '{\n  "name": "John",\n  "age": 30\n}';
      case 'escape':
        return 'Hello "World"';
      case 'unescape':
        return '"Hello \\"World\\""';
      case 'to-xml':
        return '{"root": {"name": "John", "age": 30}}';
      case 'soap-to-json':
        return '<soap:Envelope>\n  <soap:Body>\n    <getUser>\n      <id>123</id>\n    </getUser>\n  </soap:Body>\n</soap:Envelope>';
      default:
        return '{"name": "John", "age": 30}';
    }
  };

  return (
    <div className="viewer-container">
      <div className="json-tools-header">
        <h2>JSON Tools</h2>
        <p className="tools-subtitle">Validate, serialize, deserialize, and transform JSON data</p>
      </div>

      <div className="json-tools-selector">
        {tools.map((tool) => (
          <button
            key={tool.id}
            className={`tool-btn ${activeTool === tool.id ? 'active' : ''}`}
            onClick={() => {
              setActiveTool(tool.id);
              setOutput('');
              setError('');
              setAdditionalInfo(null);
            }}
          >
            <span className="tool-icon">{tool.icon}</span>
            <span className="tool-label">{tool.label}</span>
          </button>
        ))}
      </div>

      <div className="input-section">
        <h3>Input</h3>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={getPlaceholder()}
          className="input-textarea"
        />
        <button 
          onClick={handleProcess} 
          className="validate-btn"
          disabled={loading || !input.trim()}
        >
          {loading ? 'Processing...' : `Process ${tools.find(t => t.id === activeTool)?.label}`}
        </button>
        {error && <div className="error-message">{error}</div>}
        {isValid && !error && <div className="success-message">✓ Success</div>}
      </div>

      {output && (
        <>
          <div className="output-section">
            <div className="output-header">
              <h3>Output</h3>
              {additionalInfo && (
                <div className="output-info">
                  {additionalInfo.type && <span>Type: {additionalInfo.type}</span>}
                  {additionalInfo.keys && <span>Keys: {additionalInfo.keys.length}</span>}
                  {additionalInfo.length !== undefined && <span>Length: {additionalInfo.length}</span>}
                  {additionalInfo.originalSize && (
                    <span>Size: {additionalInfo.originalSize} → {additionalInfo.minifiedSize} 
                      ({additionalInfo.savings} bytes saved)
                    </span>
                  )}
                </div>
              )}
              <button 
                className="copy-btn"
                onClick={() => {
                  navigator.clipboard.writeText(output);
                  alert('Copied to clipboard!');
                }}
              >
                📋 Copy
              </button>
            </div>
            <SyntaxHighlighter 
              language={activeTool === 'to-xml' ? 'xml' : 'json'} 
              style={vscDarkPlus}
              customStyle={{ borderRadius: '8px', padding: '1rem' }}
            >
              {output}
            </SyntaxHighlighter>
            {additionalInfo?.minified && (
              <div className="minified-section">
                <h4>Minified Version</h4>
                <SyntaxHighlighter language="json" style={vscDarkPlus}>
                  {additionalInfo.minified}
                </SyntaxHighlighter>
              </div>
            )}
          </div>
          
          {/* In-content Ad - Only visible when SHOW_ADS is true and output is displayed */}
          {SHOW_ADS && (
            <div className="in-content-ad">
              <AdSense
                adFormat="auto"
                style={{ width: '100%', maxWidth: '728px', height: '90px', margin: '2rem auto', display: 'block' }}
                className="ad-incontent"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default JsonViewer;

